import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart3, Users, Eye, Monitor, Smartphone, Tablet, 
  Globe, ArrowLeft, RefreshCw, TrendingUp, Clock,
  Apple, Chrome, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform icons
const PlatformIcon = ({ platform }) => {
  switch (platform) {
    case 'web_desktop':
      return <Monitor className="w-4 h-4" />;
    case 'android_web':
    case 'android_app':
      return <Smartphone className="w-4 h-4 text-green-400" />;
    case 'ios_web':
    case 'ios_app':
      return <Apple className="w-4 h-4 text-gray-300" />;
    default:
      return <Globe className="w-4 h-4" />;
  }
};

// Simple bar chart component
const SimpleBarChart = ({ data, labelKey, valueKey, color = 'blue' }) => {
  if (!data || data.length === 0) return <p className="text-gray-500 text-sm">No data</p>;
  
  const maxValue = Math.max(...data.map(d => d[valueKey]));
  
  return (
    <div className="space-y-2">
      {data.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <div className="w-24 text-xs text-gray-400 truncate" title={item[labelKey]}>
            {item[labelKey]}
          </div>
          <div className="flex-1 bg-white/5 rounded-full h-4 overflow-hidden">
            <div 
              className={`h-full bg-${color}-500 rounded-full transition-all`}
              style={{ width: `${(item[valueKey] / maxValue) * 100}%` }}
            />
          </div>
          <div className="w-12 text-xs text-gray-300 text-right">{item[valueKey]}</div>
        </div>
      ))}
    </div>
  );
};

// Bar chart for daily views with Y-axis
const DailyChart = ({ data, label }) => {
  if (!data || data.length === 0) return <p className="text-gray-500 text-sm">No data available for this period</p>;
  
  const maxValue = Math.max(...data.map(d => d.count), 1);
  const barCount = data.length;
  const chartHeight = 160; // Fixed pixel height for chart area
  
  // Calculate nice Y-axis values
  const getYAxisTicks = (max) => {
    if (max === 0) return [0];
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    const normalized = max / magnitude;
    let step;
    if (normalized <= 2) step = magnitude * 0.5;
    else if (normalized <= 5) step = magnitude;
    else step = magnitude * 2;
    
    const ticks = [];
    for (let i = 0; i <= max; i += step) {
      ticks.push(Math.round(i));
    }
    if (ticks[ticks.length - 1] < max) {
      ticks.push(Math.round(max));
    }
    return ticks.slice(0, 5); // Max 5 ticks
  };
  
  const yTicks = getYAxisTicks(maxValue);
  const yMax = yTicks[yTicks.length - 1] || 1;
  
  // Format date for display (show day/month)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}`; // DD/MM format
    }
    return dateStr;
  };
  
  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };
  
  return (
    <div className="space-y-2">
      {/* Chart with Y-axis */}
      <div className="flex">
        {/* Y-axis labels */}
        <div className="flex flex-col justify-between pr-2 text-right" style={{ height: chartHeight }}>
          {[...yTicks].reverse().map((tick, i) => (
            <span key={i} className="text-xs text-gray-500 leading-none">
              {formatNumber(tick)}
            </span>
          ))}
        </div>
        
        {/* Chart area */}
        <div className="flex-1 relative border-l border-b border-white/20">
          {/* Horizontal grid lines */}
          {yTicks.map((tick, i) => (
            <div 
              key={i}
              className="absolute w-full border-t border-white/10"
              style={{ bottom: `${(tick / yMax) * 100}%` }}
            />
          ))}
          
          {/* Bars container */}
          <div className="flex items-end gap-1 h-full px-1" style={{ height: chartHeight }}>
            {data.map((d, i) => {
              const barHeight = (d.count / yMax) * chartHeight;
              return (
                <div 
                  key={i} 
                  className="flex-1 flex flex-col items-center justify-end group relative"
                  style={{ height: chartHeight }}
                >
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10 shadow-lg border border-white/20">
                    <div className="font-medium">{d.date}</div>
                    <div className="text-blue-400">{d.count.toLocaleString()} views</div>
                  </div>
                  {/* Bar */}
                  <div 
                    className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all duration-200 hover:from-blue-500 hover:to-blue-300 cursor-pointer"
                    style={{ 
                      height: d.count > 0 ? Math.max(barHeight, 4) : 2,
                      opacity: d.count === 0 ? 0.3 : 1
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* X-axis labels */}
      <div className="flex pl-8">
        <div className="flex-1 flex gap-1 px-1">
          {data.map((d, i) => {
            // Show fewer labels if too many bars
            const showLabel = barCount <= 14 || i % Math.ceil(barCount / 14) === 0 || i === barCount - 1;
            return (
              <div key={i} className="flex-1 text-center">
                <span className={`text-xs text-gray-500 ${showLabel ? '' : 'invisible'}`}>
                  {formatDate(d.date)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Summary below chart */}
      <div className="flex justify-between text-xs text-gray-400 pt-2 border-t border-white/10">
        <span>Total: {data.reduce((sum, d) => sum + d.count, 0).toLocaleString()} views</span>
        <span>Avg: {Math.round(data.reduce((sum, d) => sum + d.count, 0) / barCount).toLocaleString()}/day</span>
      </div>
    </div>
  );
};

const AnalyticsDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [realtimeLoading, setRealtimeLoading] = useState(false);
  const [period, setPeriod] = useState('7');
  const [dashboardData, setDashboardData] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchDashboardData();
    fetchRealtimeData();
    
    // Auto-refresh realtime every 30 seconds
    const interval = setInterval(fetchRealtimeData, 30000);
    return () => clearInterval(interval);
  }, [user, navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, [period]);

  const fetchDashboardData = async () => {
    setLoading(true);
    const token = localStorage.getItem('session_token');
    
    try {
      const response = await axios.get(`${API}/admin/analytics/dashboard?days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRealtimeData = async () => {
    setRealtimeLoading(true);
    const token = localStorage.getItem('session_token');
    
    try {
      const response = await axios.get(`${API}/admin/analytics/realtime`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRealtimeData(response.data);
    } catch (error) {
      console.error('Error fetching realtime data:', error);
    } finally {
      setRealtimeLoading(false);
    }
  };

  const getPlatformLabel = (platform) => {
    const labels = {
      'web_desktop': 'Desktop (Browser)',
      'android_web': 'Android (Browser)',
      'android_app': 'Android (App)',
      'ios_web': 'iOS (Browser)',
      'ios_app': 'iOS (App)',
      'web_mobile': 'Mobile Web (Other)',
      'mobile_app': 'Mobile App'
    };
    return labels[platform] || platform;
  };

  const getScreenCategoryLabel = (category) => {
    const labels = {
      'mobile': 'Mobile (<576px)',
      'tablet-sm': 'Small Tablet (576-767px)',
      'tablet': 'Tablet (768-991px)',
      'desktop': 'Desktop (992-1199px)',
      'desktop-lg': 'Large Desktop (1200-1399px)',
      'desktop-xl': 'Extra Large (1400px+)'
    };
    return labels[category] || category;
  };

  if (loading && !dashboardData) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-white">Loading analytics...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-blue-400" />
              Analytics Dashboard
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Today</SelectItem>
                <SelectItem value="1">Yesterday</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              onClick={() => { fetchDashboardData(); fetchRealtimeData(); }}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs - Horizontal scrollable on mobile */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
          <Button
            onClick={() => setActiveTab('overview')}
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'overview' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}`}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Overview
          </Button>
          <Button
            onClick={() => setActiveTab('realtime')}
            variant={activeTab === 'realtime' ? 'default' : 'outline'}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'realtime' ? 'bg-green-600 hover:bg-green-700' : 'border-white/20 text-white hover:bg-white/10'}`}
          >
            <Activity className="w-4 h-4 mr-2" />
            Real-Time
          </Button>
          <Button
            onClick={() => setActiveTab('platforms')}
            variant={activeTab === 'platforms' ? 'default' : 'outline'}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'platforms' ? 'bg-purple-600 hover:bg-purple-700' : 'border-white/20 text-white hover:bg-white/10'}`}
          >
            <Monitor className="w-4 h-4 mr-2" />
            Platforms
          </Button>
          <Button
            onClick={() => setActiveTab('technology')}
            variant={activeTab === 'technology' ? 'default' : 'outline'}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'technology' ? 'bg-orange-600 hover:bg-orange-700' : 'border-white/20 text-white hover:bg-white/10'}`}
          >
            <Chrome className="w-4 h-4 mr-2" />
            Technology
          </Button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboardData && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="glass-dark border-white/10">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Page Views</p>
                      <p className="text-3xl font-bold text-white">{dashboardData.summary.total_page_views.toLocaleString()}</p>
                    </div>
                    <Eye className="w-10 h-10 text-blue-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-dark border-white/10">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Unique Visitors</p>
                      <p className="text-3xl font-bold text-white">{dashboardData.summary.unique_visitors.toLocaleString()}</p>
                    </div>
                    <Users className="w-10 h-10 text-green-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-dark border-white/10">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Sessions</p>
                      <p className="text-3xl font-bold text-white">{dashboardData.summary.unique_sessions.toLocaleString()}</p>
                    </div>
                    <Clock className="w-10 h-10 text-purple-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-dark border-white/10">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Events</p>
                      <p className="text-3xl font-bold text-white">{dashboardData.summary.total_events.toLocaleString()}</p>
                    </div>
                    <Activity className="w-10 h-10 text-orange-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Daily Chart */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Page Views Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <DailyChart data={dashboardData.daily_views} label="Page Views" />
              </CardContent>
            </Card>

            {/* Countries */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Top Countries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboardData.countries && dashboardData.countries.length > 0 ? (
                    dashboardData.countries.slice(0, 10).map((country, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <div className="flex items-center gap-2 flex-1 mr-4">
                          {country.code && (
                            <img 
                              src={`https://flagcdn.com/24x18/${country.code.toLowerCase()}.png`}
                              alt={country.name}
                              className="w-6 h-4 object-cover rounded-sm"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <span className="text-gray-300">{country.name}</span>
                        </div>
                        <span className="text-white font-medium">{country.count.toLocaleString()}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-4">No country data available yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Top Pages */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Top Pages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboardData.top_pages.slice(0, 10).map((page, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <span className="text-gray-300 truncate flex-1 mr-4">{page.path}</span>
                      <span className="text-white font-medium">{page.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Real-Time Tab */}
        {activeTab === 'realtime' && (
          <>
            {/* Real-time Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="glass-dark border-green-500/30 bg-green-500/5">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-400 text-sm">Active Now (5 min)</p>
                      <p className="text-4xl font-bold text-white">{realtimeData?.active_users_5min || 0}</p>
                    </div>
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-dark border-blue-500/30 bg-blue-500/5">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-400 text-sm">Views (30 min)</p>
                      <p className="text-4xl font-bold text-white">{realtimeData?.page_views_30min || 0}</p>
                    </div>
                    <Eye className="w-10 h-10 text-blue-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-dark border-white/10">
                <CardContent className="pt-6">
                  <p className="text-gray-400 text-sm mb-3">Real-time by Platform</p>
                  <div className="space-y-2">
                    {realtimeData?.platforms?.map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-gray-300 text-sm flex items-center gap-2">
                          <PlatformIcon platform={p.name} />
                          {getPlatformLabel(p.name)}
                        </span>
                        <span className="text-white font-medium">{p.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Live Activity Feed */}
            <Card className="glass-dark border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Live Activity
                </CardTitle>
                <Button
                  onClick={fetchRealtimeData}
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  disabled={realtimeLoading}
                >
                  <RefreshCw className={`w-4 h-4 ${realtimeLoading ? 'animate-spin' : ''}`} />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {realtimeData?.recent_pages?.map((page, idx) => (
                    <div key={idx} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                      <PlatformIcon platform={page.platform} />
                      <span className="text-gray-300 flex-1 truncate">{page.path}</span>
                      <span className="text-gray-500 text-xs">{page.browser}</span>
                      <span className="text-gray-500 text-xs">{page.os}</span>
                      <span className="text-gray-500 text-xs">
                        {new Date(page.time).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Platforms Tab */}
        {activeTab === 'platforms' && dashboardData && (
          <div className="space-y-6">
            {/* Platform Legend */}
            <Card className="glass-dark border-blue-500/20 bg-blue-500/5">
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-start gap-2">
                    <Monitor className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Desktop (Browser)</p>
                      <p className="text-gray-400 text-xs">PC/Mac web browsers</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Smartphone className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Android/iOS (Browser)</p>
                      <p className="text-gray-400 text-xs">Mobile web browsers (Chrome, Safari)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Tablet className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Android/iOS (App)</p>
                      <p className="text-gray-400 text-xs">Native mobile app traffic</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Platform Breakdown */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-400" />
                  Platform Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData.platforms.map((p, idx) => {
                    const total = dashboardData.summary.total_page_views;
                    const percent = ((p.count / total) * 100).toFixed(1);
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <PlatformIcon platform={p.name} />
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-300 text-sm">{getPlatformLabel(p.name)}</span>
                            <span className="text-gray-400 text-sm">{percent}%</span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-white font-medium w-16 text-right">{p.count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Device Types */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-green-400" />
                  Device Types
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData.devices.map((d, idx) => {
                    const total = dashboardData.summary.total_page_views;
                    const percent = ((d.count / total) * 100).toFixed(1);
                    const icons = {
                      'desktop': <Monitor className="w-4 h-4 text-blue-400" />,
                      'android': <Smartphone className="w-4 h-4 text-green-400" />,
                      'ios': <Apple className="w-4 h-4 text-gray-300" />,
                      'mobile': <Smartphone className="w-4 h-4 text-purple-400" />
                    };
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        {icons[d.name] || <Globe className="w-4 h-4" />}
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-300 text-sm capitalize">{d.name}</span>
                            <span className="text-gray-400 text-sm">{percent}%</span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-white font-medium w-16 text-right">{d.count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Screen Categories */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-purple-400" />
                  Screen Sizes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData.screen_categories.map((s, idx) => {
                    const total = dashboardData.screen_categories.reduce((sum, item) => sum + item.count, 0);
                    const percent = ((s.count / total) * 100).toFixed(1);
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-300 text-sm">{getScreenCategoryLabel(s.name)}</span>
                            <span className="text-gray-400 text-sm">{percent}%</span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-purple-500 rounded-full"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-white font-medium w-16 text-right">{s.count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Screen Resolutions */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Top Screen Resolutions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboardData.resolutions.slice(0, 10).map((r, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1">
                      <span className="text-gray-300 text-sm font-mono">{r.name}</span>
                      <span className="text-white">{r.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          </div>
        )}

        {/* Technology Tab */}
        {activeTab === 'technology' && dashboardData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Browsers */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Chrome className="w-5 h-5 text-blue-400" />
                  Browsers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData.browsers.map((b, idx) => {
                    const total = dashboardData.browsers.reduce((sum, item) => sum + item.count, 0);
                    const percent = ((b.count / total) * 100).toFixed(1);
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-300 text-sm">{b.name}</span>
                            <span className="text-gray-400 text-sm">{percent}%</span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-white font-medium w-16 text-right">{b.count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Operating Systems */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-green-400" />
                  Operating Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData.operating_systems.map((os, idx) => {
                    const total = dashboardData.operating_systems.reduce((sum, item) => sum + item.count, 0);
                    const percent = ((os.count / total) * 100).toFixed(1);
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-300 text-sm">{os.name}</span>
                            <span className="text-gray-400 text-sm">{percent}%</span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-white font-medium w-16 text-right">{os.count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Languages */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Languages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboardData.languages.map((l, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1">
                      <span className="text-gray-300 text-sm">{l.code}</span>
                      <span className="text-white">{l.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Referrers */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Top Referrers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboardData.referrers.length > 0 ? (
                    dashboardData.referrers.map((r, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1">
                        <span className="text-gray-300 text-sm truncate flex-1 mr-2" title={r.url}>
                          {r.url.replace(/^https?:\/\//, '').slice(0, 40)}
                        </span>
                        <span className="text-white">{r.count.toLocaleString()}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No referrer data</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        </div>
      </div>
    </Layout>
  );
};

export default AnalyticsDashboard;
